// src/components/MyLibraryMaterialsPage.js
import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { useRegion } from '../context/RegionContext';
import { auth } from '../firebaseConfig';
import { API_BASE_URL } from '../services/api';
import './MyLibraryMaterials.css';
import ContextMenu from './ContextMenu';
import { gotoMaterialUrl } from '../utils/nezoApp';

/* ─── Helpers (same as BuildUpPage) ─────────────────────────────────────── */

const getCategoryIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('piling') || n.includes('pile'))    return '🏗️';
  if (n.includes('foundation'))                       return '🧱';
  if (n.includes('concrete'))                         return '🏭';
  if (n.includes('steel') || n.includes('metal'))    return '⚙️';
  if (n.includes('timber') || n.includes('wood'))    return '🌲';
  if (n.includes('insulation'))                       return '🏠';
  if (n.includes('window') || n.includes('glazing')) return '🪟';
  if (n.includes('roof'))                             return '🏘️';
  if (n.includes('door'))                             return '🚪';
  if (n.includes('paint') || n.includes('coating'))  return '🎨';
  if (n.includes('floor'))                            return '◼️';
  if (n.includes('wall'))                             return '▢';
  return '📦';
};

const fmt3 = (v) =>
  v === null || v === undefined || v === '' ? '-' : isNaN(+v) ? v : (+v).toFixed(3);

const fmtR = (v) =>
  v === null || v === undefined || v === '' ? '-' : isNaN(+v) ? '-' : (+v).toFixed(2);

/* ─── Component ──────────────────────────────────────────────────────────── */

function MyLibraryMaterialsPage() {
  const { categoryId }     = useParams();
  const location           = useLocation();
  const navigate           = useNavigate();
  const { user }           = useContext(AuthContext);
  const { selectedRegion } = useRegion();

  const categoryName = location.state?.categoryName || categoryId;

  const [materials, setMaterials]       = useState([]);
  const [filtered, setFiltered]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [searchTerm, setSearchTerm]     = useState('');
  const [contextMenu, setContextMenu]   = useState(null);
  const regionForLinks = selectedRegion || 'vquest';

  /* ── Fetch materials ── */
  const loadMaterials = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(
        `${API_BASE_URL}/private-materials/materials/${encodeURIComponent(categoryId)}`,
        {
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${token}`,
            'x-region':      selectedRegion || 'vquest',
            'x-user-role':   user.role  || 'user',
            'x-user-id':     user.uid,
            'x-user-email':  user.email,
          },
        }
      );

      if (!res.ok) throw new Error(`Server error: ${res.status}`);
      const data = await res.json();
      const mats = data.materials || [];
      setMaterials(mats);
      setFiltered(mats);
    } catch (err) {
      console.error('❌ Error loading private materials:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user, selectedRegion, categoryId]);

  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  /* ── Search filter ── */
  useEffect(() => {
    if (!searchTerm) { setFiltered(materials); return; }
    const q = searchTerm.toLowerCase();
    setFiltered(
      materials.filter((m) =>
        (m.QuantityUnitName || '').toLowerCase().includes(q) ||
        (m.Description      || '').toLowerCase().includes(q)
      )
    );
  }, [searchTerm, materials]);

  /* ── Loading ── */
  if (loading) return (
    <div className="mlm-page">
      <div className="mlm-toolbar">
        <button className="mlm-back-btn" onClick={() => navigate('/user/my-library')}>← Back</button>
        <h1>My Library — {categoryName}</h1>
      </div>
      <div className="mlm-loading">
        <div className="mlm-loading__spinner" />
        <p>Loading your private materials…</p>
      </div>
    </div>
  );

  return (
    <div className="mlm-page">

      {/* Toolbar */}
      <div className="mlm-toolbar">
        <button className="mlm-back-btn" onClick={() => navigate('/user/my-library')}>
          ← My Library
        </button>
        <div className="mlm-toolbar__title">
          <span className="mlm-toolbar__icon">{getCategoryIcon(categoryName)}</span>
          <h1>{categoryName}</h1>
          <span className="mlm-toolbar__badge">🔒 Private</span>
        </div>
        <button className="mlm-refresh-btn" onClick={loadMaterials}>🔄</button>
      </div>

      {/* Search + count */}
      <div className="mlm-controls">
        <div className="mlm-search-wrap">
          <input
            type="text"
            className="mlm-search"
            placeholder="Search materials…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="mlm-search-clear" onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
        <span className="mlm-count">
          Showing {filtered.length} of {materials.length} materials
        </span>
      </div>

      {/* Error */}
      {error && (
        <div className="mlm-error">
          <span>⚠️ {error}</span>
          <button onClick={loadMaterials}>Retry</button>
        </div>
      )}

      {/* Empty */}
      {!error && filtered.length === 0 && (
        <div className="mlm-empty">
          <div className="mlm-empty__icon">{getCategoryIcon(categoryName)}</div>
          <h3>{searchTerm ? `No results for "${searchTerm}"` : 'No private materials in this category'}</h3>
          {!searchTerm && (
            <button className="mlm-empty__btn" onClick={() => navigate('/composite-requests')}>
              + Build a Composite
            </button>
          )}
        </div>
      )}

      {/* Materials table */}
      {filtered.length > 0 && (
        <div className="mlm-table-wrap">
          <table className="mlm-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Unit</th>
                <th className="num">CO₂ (kg)</th>
                <th className="num">Biogenic (kg)</th>
                <th className="num">Weight (kg)</th>
                <th className="num">R-Value</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr
                  key={m.QuantityUnitId}
                  className="mlm-row"
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({
                      x: e.clientX,
                      y: e.clientY,
                      items: [
                        m.NoBuildUp
                          ? { icon: '🧱', label: 'Add build-up', onClick: () => navigate('/user/my-composites', { state: { updateQuantityUnitId: m.QuantityUnitId } }) }
                          : { icon: '🔍', label: 'Breakdown', onClick: () => navigate(`/breakdown/${m.QuantityUnitId}`) },
                        {
                          icon: '📄', label: 'Edit Copy', dividerAfter: true,
                          onClick: () => navigate('/composite-requests', { state: { prefill: { materialId: m.QuantityUnitId, materialName: m.QuantityUnitName } } }),
                        },
                        { icon: '↗', label: 'Go to Nezo', onClick: () => window.open(gotoMaterialUrl(m.QuantityUnitId, regionForLinks, m.QuantityUnitName, m.Environment), '_blank', 'noopener') },
                        { icon: '⧉', label: 'Copy material ID', onClick: () => navigator.clipboard?.writeText(m.QuantityUnitId) },
                      ],
                    });
                  }}
                >
                  <td className="mlm-row__name">
                    <span className="mlm-row__lock">🔒</span>
                    {m.QuantityUnitName}
                    {!!m.NoBuildUp && (
                      <span
                        title="Cost only so far — priced in Nezo from the Rate Library. Add carbon, biogenic and weight any time."
                        style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74', whiteSpace: 'nowrap' }}
                      >
                        No build-up yet
                      </span>
                    )}
                  </td>
                  <td className="mlm-row__desc">{m.Description || '—'}</td>
                  <td>{m.QuantityTypeName || '—'}</td>
                  <td className="num">{fmt3(m.vquest_Co2)}</td>
                  <td className="num">{fmt3(m.vquest_Co2_Biogenic)}</td>
                  <td className="num">{fmt3(m.vquest_Weight)}</td>
                  <td className={`num ${m.RValue ? 'has-rvalue' : ''}`}>{fmtR(m.RValue)}</td>
                  <td>
                    <div className="mlm-actions">
                      {m.NoBuildUp ? (
                        // Same material id — its Nezo mappings and Rate Library cost stay attached
                        <button
                          className="mlm-btn mlm-btn--breakdown"
                          onClick={() => navigate('/user/my-composites', { state: { updateQuantityUnitId: m.QuantityUnitId } })}
                        >
                          Add build-up
                        </button>
                      ) : (
                      <button
                        className="mlm-btn mlm-btn--breakdown"
                        onClick={() => navigate(`/breakdown/${m.QuantityUnitId}`)}
                      >
                        Breakdown
                      </button>
                      )}
                      <button
                        className="mlm-btn mlm-btn--edit"
                        onClick={() => navigate('/composite-requests', {
                          state: {
                            prefill: {
                              materialId:   m.QuantityUnitId,
                              materialName: m.QuantityUnitName,
                            },
                          },
                        })}
                      >
                        Edit Copy
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ContextMenu menu={contextMenu} onClose={() => setContextMenu(null)} />
    </div>
  );
}

export default MyLibraryMaterialsPage;